/**
 * Shared Label Guidance for AI Extraction Prompts
 *
 * Single source of truth for all label-to-field mappings and disambiguation rules.
 * Both GPT-4o and Claude Sonnet prompts import from here to ensure consistent extraction.
 *
 * WHY THIS EXISTS:
 * Equipment nameplates use wildly different label formats across manufacturers.
 * Without explicit guidance, AI models can:
 *   1. Miss fields because the label doesn't match expected format
 *   2. Confuse adjacent fields (e.g., "RECOVERY 145 GPH" bleeding into "CAPACITY")
 *   3. Produce inconsistent results between GPT and Claude
 *
 * HOW TO USE:
 * Import the prompt sections you need and interpolate them into your AI prompt string.
 */

// ============================================================================
// FIELD LABEL VARIANTS — what labels to look for on nameplates
// ============================================================================

/**
 * Maps JSON field paths to the label variants found on equipment nameplates.
 * Each entry includes:
 *   - field: the JSON path in the extraction schema
 *   - labels: text patterns to look for on the nameplate
 *   - unit: the expected unit suffix
 *   - notes: disambiguation or special handling rules
 *   - notThisField: fields that are commonly confused with this one
 */
export const FIELD_LABEL_MAP = {
  // ── IDENTIFICATION ──────────────────────────────────────────────────
  manufacturer: {
    field: 'manufacturer',
    labels: [
      'manufacturer name (usually largest text or logo)',
      'brand name',
      'company name at top or bottom of label',
    ],
    notes: 'Usually the largest/most prominent text. May also appear in fine print address block.',
  },
  model_number: {
    field: 'model_number',
    labels: [
      'MODEL NO.', 'MODEL', 'MOD.', 'MOD NO.', 'M/N', 'MODEL NUMBER',
      'CAT NO.', 'CATALOG NO.', 'TYPE',
    ],
    notes: 'Do NOT extract numbers from model number as capacity, BTU, or other specs. "SWR125N" → model is "SWR125N", the 125 is NOT capacity.',
  },
  serial_number: {
    field: 'serial_number',
    labels: [
      'SERIAL NO.', 'SERIAL', 'SER.', 'SER NO.', 'S/N', 'SERIAL NUMBER',
    ],
    notes: 'May contain date codes. Some manufacturers have multiple serial numbers on one label (storage unit serial vs. heater serial).',
  },
  series: {
    field: 'series',
    labels: [
      'SERIES', 'PRODUCT LINE', 'LINE',
    ],
    notes: '"SERIES 100" → series: "100". This is NOT a capacity value. Also check model number prefix (e.g., "SWR" from SWR125N).',
    notThisField: ['capacity.gallons', 'capacity.btu'],
  },

  // ── CAPACITY (TANK / VOLUME) ────────────────────────────────────────
  capacity_gallons: {
    field: 'capacity.gallons',
    labels: [
      'CAPACITY RATED/MEASURED XX/YY gal',
      'CAPACITY', 'Capacity:',
      'TANK SIZE', 'Tank Size:',
      'XX GAL', 'XX GALLONS',
      'NOMINAL CAPACITY',
      'RATED STORAGE VOLUME',
      'STORAGE CAPACITY',
      'NET RATED STORAGE',
      'USABLE STORAGE',
      'US CAPACITY',
    ],
    unit: 'gal, gallons, GAL, US gal',
    notes: [
      'DUAL VALUE FORMAT: "CAPACITY RATED/MEASURED 65/64 gal" → use FIRST number (65 = rated capacity).',
      'EXPLICIT LABELS ALWAYS WIN: If ANY capacity label exists, use that value. NEVER infer from model number.',
      'LAST RESORT: Only infer from model number (e.g., SWR125N → 125) if NO explicit capacity label exists.',
    ].join(' '),
    notThisField: [
      'water.recovery_rate_gph — "RECOVERY XX GPH" is flow rate, NOT tank size',
      'water_heater.first_hour_rating — "FIRST HOUR RATING" is NOT tank capacity',
      'series — "SERIES 100" is a product line, NOT 100 gallons',
    ],
  },
  tank_capacity_gallons: {
    field: 'water_heater.tank_capacity_gallons',
    labels: ['(same as capacity.gallons — populate BOTH fields from the same label)'],
    notes: 'This should match capacity.gallons for water heaters.',
  },

  // ── CAPACITY (BTU / THERMAL) ────────────────────────────────────────
  btu_input: {
    field: 'fuel.input_btu_hr',
    labels: [
      'INPUT RATING', 'INPUT', 'INPUT RATE',
      'BTU INPUT', 'BTU/HR INPUT',
      'RATED INPUT', 'GAS INPUT',
      'XX BTU/HR', 'XX,000 BTU/HR',
    ],
    unit: 'BTU/HR, BTU/H, BTUH, BTU',
    notes: 'For dual-fuel labels, extract from the FACTORY EQUIPPED fuel section only. This is the RATED/MAXIMUM input.',
    notThisField: [
      'fuel.minimum_input_btu_hr — "MINIMUM INPUT RATING" is a SEPARATE, LOWER number',
      'capacity.btu — output/cooling BTU (different from fuel input)',
    ],
  },
  minimum_input_btu_hr: {
    field: 'fuel.minimum_input_btu_hr',
    labels: [
      'MINIMUM INPUT RATING', 'MIN INPUT RATING',
      'MIN INPUT', 'MINIMUM INPUT',
      'MINIMUM RATE', 'LOW FIRE',
      'TURNDOWN',
    ],
    unit: 'BTU/HR, BTU/H, BTUH, BTU',
    notes: 'Always LOWER than input_btu_hr. Indicates modulating burner capability. For dual-fuel labels, extract from the FACTORY EQUIPPED fuel section only.',
    notThisField: [
      'fuel.input_btu_hr — "INPUT RATING" (without "MINIMUM") is the rated/max input',
    ],
  },

  // ── ELECTRICAL ──────────────────────────────────────────────────────
  voltage: {
    field: 'electrical.voltage',
    labels: [
      'VOLTAGE', 'VOLTS', 'V', 'RATED VOLTAGE',
      'SUPPLY VOLTAGE', 'LINE VOLTAGE',
      'XX V', 'XXX/YYY V',
    ],
    unit: 'V, VOLTS, VAC',
    notes: 'Common values: 120, 208, 230, 240, 277, 460, 480. If "120/240" → use voltage_range.',
    notThisField: ['electrical.phase — "1" next to voltage is phase, not voltage'],
  },
  phase: {
    field: 'electrical.phase',
    labels: [
      'PHASE', 'PH', 'PH.', 'Ø',
    ],
    unit: '(none — just 1 or 3)',
    notes: 'Only valid values are 1 or 3. If you see a different number, it was likely misread from an adjacent field.',
    notThisField: ['electrical.voltage — numbers near phase labels belong to phase, not voltage'],
  },
  fla: {
    field: 'electrical.fla',
    labels: [
      'FLA', 'F.L.A.', 'FULL LOAD AMPS', 'FULL LOAD',
    ],
    unit: 'A, AMPS',
    notes: null,
  },
  rla: {
    field: 'electrical.rla',
    labels: [
      'RLA', 'R.L.A.', 'RATED LOAD AMPS', 'RATED AMPS',
    ],
    unit: 'A, AMPS',
    notes: null,
  },
  lra: {
    field: 'electrical.lra',
    labels: [
      'LRA', 'L.R.A.', 'LOCKED ROTOR AMPS', 'LOCKED ROTOR',
    ],
    unit: 'A, AMPS',
    notes: 'Always higher than FLA/RLA. If lower, likely a misread.',
  },
  mca: {
    field: 'electrical.mca',
    labels: [
      'MCA', 'M.C.A.', 'MIN CIRCUIT AMPACITY', 'MINIMUM CIRCUIT AMPACITY',
    ],
    unit: 'A, AMPS',
    notes: 'Used for electrical planning — wire sizing.',
  },
  mocp: {
    field: 'electrical.mocp',
    labels: [
      'MOCP', 'M.O.C.P.', 'MAX OVERCURRENT PROTECTION',
      'MAX FUSE', 'MAX BREAKER', 'MAX CIRCUIT BREAKER',
    ],
    unit: 'A, AMPS',
    notes: 'Used for electrical planning — breaker sizing. Always a standard breaker size (15, 20, 25, 30, 35, 40, 45, 50, 60, etc.).',
  },

  // ── WATER / FLOW ────────────────────────────────────────────────────
  recovery_rate_gph: {
    field: 'water.recovery_rate_gph',
    labels: [
      'RECOVERY', 'RECOVERY RATE',
      'RECOVERY @ 100°F RISE', 'RECOVERY @ 100F RISE',
      'RECOVERY AT 100 DEGREE RISE',
      'GPH RECOVERY', 'RECOVERY GPH',
    ],
    unit: 'GPH, GAL/HR, GALLONS PER HOUR',
    notes: 'This is a FLOW RATE (gallons per hour), NOT a volume/tank size. Do NOT use this value for capacity.gallons.',
    notThisField: [
      'capacity.gallons — recovery rate is GPH (flow), NOT tank volume (GAL)',
      'water_heater.tank_capacity_gallons — this is NOT the same measurement',
    ],
  },

  // ── FUEL ────────────────────────────────────────────────────────────
  fuel_type: {
    field: 'fuel.type',
    labels: [
      'FACTORY EQUIPPED FOR', 'FUEL TYPE', 'GAS TYPE',
      'NATURAL GAS', 'PROPANE', 'LP', 'LPG', 'OIL',
    ],
    notes: '"FACTORY EQUIPPED FOR NATURAL GAS" → fuel_type: "natural_gas". This also determines which section to read specs from on dual-fuel labels.',
  },
  gas_pressure: {
    field: 'fuel.gas_pressure',
    labels: [
      'MAX INLET GAS PRESSURE', 'MIN INLET GAS PRESSURE',
      'MANIFOLD PRESSURE', 'GAS PRESSURE',
      'INLET PRESSURE', 'SUPPLY PRESSURE',
    ],
    unit: 'IN WC, "WC, IN W.C., INCHES WATER COLUMN',
    notes: 'Extract as string with the pressure value. May have both min and max listed separately.',
  },

  // ── WATER HEATER SPECIFIC ──────────────────────────────────────────
  thermal_efficiency: {
    field: 'capacity.thermal_efficiency',
    labels: [
      'THERMAL EFFICIENCY', 'EFFICIENCY',
      'THERMAL EFF.',
    ],
    unit: '%',
    notes: 'Percentage value, typically 80-99%.',
    notThisField: ['water_heater.energy_factor — EF is a different scale (0.5-1.0 range)'],
  },
  energy_factor: {
    field: 'water_heater.energy_factor',
    labels: [
      'ENERGY FACTOR', 'EF', 'E.F.',
    ],
    unit: '(dimensionless, typically 0.5-1.0)',
    notes: 'Older efficiency standard. A decimal number, NOT a percentage.',
  },
  uef_rating: {
    field: 'water_heater.uef_rating',
    labels: [
      'UEF', 'UNIFORM ENERGY FACTOR', 'U.E.F.',
    ],
    unit: '(dimensionless, typically 0.5-4.0)',
    notes: 'Newer efficiency standard replacing EF. Values > 1.0 indicate heat pump water heaters.',
  },
  first_hour_rating: {
    field: 'water_heater.first_hour_rating',
    labels: [
      'FIRST HOUR RATING', 'FHR', '1ST HOUR RATING',
      'FIRST HOUR DELIVERY',
    ],
    unit: 'GAL, GALLONS',
    notes: 'This is NOT the same as tank capacity. It measures how much hot water the unit can deliver in the first hour of use.',
    notThisField: [
      'capacity.gallons — first hour rating ≠ tank capacity',
    ],
  },
  standby_loss: {
    field: 'capacity.standby_loss',
    labels: [
      'STANDBY LOSS', 'STANDBY HEAT LOSS',
    ],
    unit: 'BTU/HR/FT², BTU/HR',
    notes: 'Heat loss while idle. This is NOT an input or capacity rating.',
    notThisField: ['fuel.input_btu_hr', 'capacity.btu'],
  },

  // ── PHYSICAL ────────────────────────────────────────────────────────
  weight: {
    field: 'physical.weight_lbs',
    labels: ['WEIGHT', 'NET WEIGHT', 'SHIPPING WEIGHT', 'WT.'],
    unit: 'LBS, LB, KG',
    notes: null,
  },

  // ── CERTIFICATIONS ──────────────────────────────────────────────────
  certifications: {
    field: 'certifications',
    labels: [
      'UL', 'UL LISTED', 'CSA', 'ETL', 'NSF', 'ENERGY STAR',
      'AHRI CERTIFIED', 'DESIGN CERTIFIED', 'ASME',
      'CRN', 'AGA', 'CE',
    ],
    notes: 'Look for certification marks/logos as well as text labels.',
  },
} as const

// ============================================================================
// DISAMBIGUATION RULES — critical for preventing cross-field contamination
// ============================================================================

/**
 * Rules that prevent the AI from confusing adjacent fields.
 * These are the most common misread patterns seen in production.
 */
export const DISAMBIGUATION_RULES = [
  {
    rule: 'RECOVERY ≠ CAPACITY',
    description: '"RECOVERY @ 100°F RISE 145 GPH" means the unit can heat 145 gallons per hour. This is a FLOW RATE. The TANK CAPACITY is a separate label (e.g., "CAPACITY RATED/MEASURED 65/64 gal"). These are DIFFERENT numbers — do NOT use the recovery value as the gallons capacity.',
    wrongExample: 'RECOVERY 145 GPH → capacity.gallons: 145',
    rightExample: 'RECOVERY 145 GPH → water.recovery_rate_gph: 145, capacity.gallons: (read from CAPACITY label)',
  },
  {
    rule: 'FIRST HOUR RATING ≠ CAPACITY',
    description: '"FIRST HOUR RATING 180 GAL" is how much hot water the unit delivers in the first hour. It is usually LARGER than the tank capacity because of recovery. Do NOT use it as tank capacity.',
    wrongExample: 'FIRST HOUR RATING 180 → capacity.gallons: 180',
    rightExample: 'FIRST HOUR RATING 180 → water_heater.first_hour_rating: 180',
  },
  {
    rule: 'SERIES ≠ CAPACITY',
    description: '"SERIES 100" is a product line name. It is NOT a capacity or BTU value.',
    wrongExample: 'SERIES 100 → capacity.gallons: 100',
    rightExample: 'SERIES 100 → series: "100"',
  },
  {
    rule: 'MODEL NUMBER ≠ CAPACITY',
    description: 'Numbers in a model number (e.g., "125" in "SWR125N") are part of the model identifier. NEVER extract them as capacity, BTU, or any other spec unless there is a separate explicit label for that value.',
    wrongExample: 'MODEL NO. SWR125N → capacity.gallons: 125',
    rightExample: 'MODEL NO. SWR125N → model_number: "SWR125N"',
  },
  {
    rule: 'INPUT RATING ≠ MINIMUM INPUT RATING',
    description: '"INPUT RATING 125,000 BTU/HR" and "MINIMUM INPUT RATING 25,000 BTU/HR" are TWO DIFFERENT fields. Input rating is the maximum/rated power. Minimum input rating is the low-fire modulation point. The minimum is ALWAYS a smaller number.',
    wrongExample: 'INPUT RATING 125,000 → fuel.minimum_input_btu_hr: 125000',
    rightExample: 'INPUT RATING 125,000 → fuel.input_btu_hr: 125000; MINIMUM INPUT RATING 25,000 → fuel.minimum_input_btu_hr: 25000',
  },
  {
    rule: 'STANDBY LOSS ≠ BTU INPUT',
    description: '"STANDBY LOSS 0.3 BTU/HR/FT²" is heat loss while idle. It is NOT the input rating and is a very small number compared to input BTU.',
    wrongExample: 'STANDBY LOSS 0.3 → fuel.input_btu_hr: 0.3',
    rightExample: 'STANDBY LOSS 0.3 → (informational, low priority)',
  },
  {
    rule: 'MAWP ≠ CAPACITY',
    description: '"MAWP WATER 160 PSI" is Maximum Allowable Working Pressure. It is NOT a capacity or temperature value.',
    wrongExample: 'MAWP 160 → capacity.gallons: 160',
    rightExample: 'MAWP 160 → capacity.max_working_psi: 160',
  },
  {
    rule: 'PHASE ≠ VOLTAGE',
    description: 'On multi-column labels, "PHASE 1" and "VOLTAGE 120" are often on the same row. The "1" belongs to PHASE, not to VOLTAGE.',
    wrongExample: 'VOLTAGE 120  PHASE 1 → voltage: 1201 (reading across)',
    rightExample: 'VOLTAGE 120  PHASE 1 → voltage: 120, phase: 1 (reading by proximity)',
  },
] as const

// ============================================================================
// PROMPT BUILDERS — generate prompt sections for AI models
// ============================================================================

/**
 * Generate the disambiguation section for AI prompts.
 * This section explicitly tells the AI what NOT to confuse.
 */
export function buildDisambiguationPrompt(): string {
  const lines = [
    '## FIELD DISAMBIGUATION (CRITICAL — DO NOT CONFUSE THESE)',
    '',
    'Equipment labels often have multiple numbers close together. These rules prevent cross-contamination:',
    '',
  ]

  for (const rule of DISAMBIGUATION_RULES) {
    lines.push(`### ${rule.rule}`)
    lines.push(rule.description)
    lines.push(`  WRONG: ${rule.wrongExample}`)
    lines.push(`  RIGHT: ${rule.rightExample}`)
    lines.push('')
  }

  return lines.join('\n')
}

/**
 * Generate the tank capacity label guidance section.
 * Used by both GPT and Claude prompts.
 */
export function buildCapacityGuidance(): string {
  return `## TANK CAPACITY vs. RECOVERY RATE (CRITICAL DISTINCTION)

These are TWO DIFFERENT measurements that appear on water heater labels:

**TANK CAPACITY** (→ capacity.gallons AND water_heater.tank_capacity_gallons):
  Labels: "CAPACITY", "CAPACITY RATED/MEASURED", "TANK SIZE", "STORAGE CAPACITY",
          "NOMINAL CAPACITY", "RATED STORAGE VOLUME", "NET RATED STORAGE"
  Unit: GAL, GALLONS, US GAL
  What it means: How many gallons the tank holds (a VOLUME)
  Dual value format: "CAPACITY RATED/MEASURED 65/64 gal" → use FIRST number (65)

**RECOVERY RATE** (→ water.recovery_rate_gph):
  Labels: "RECOVERY", "RECOVERY RATE", "RECOVERY @ 100°F RISE", "RECOVERY GPH"
  Unit: GPH, GAL/HR (gallons per HOUR)
  What it means: How fast the unit heats water (a FLOW RATE)

These numbers are often DIFFERENT even though both involve "gallons":
  Example: RECOVERY 145 GPH (flow rate) vs. CAPACITY 65 GAL (tank size)
  The 145 is NOT the tank capacity — it goes to recovery_rate_gph.
  The 65 IS the tank capacity — it goes to capacity.gallons.

RULE: If you see "RECOVERY" with "GPH" → recovery_rate_gph (NEVER capacity.gallons)
RULE: If you see "CAPACITY" with "GAL" → capacity.gallons (the actual tank volume)`
}

/**
 * Generate the dual-fuel guidance section.
 * Used by both GPT and Claude prompts.
 */
export function buildDualFuelGuidance(): string {
  return `## DUAL-FUEL / DUPLICATE SPECIFICATION SECTIONS

Many water heaters and gas appliances show TWO side-by-side specification blocks — one for
NATURAL GAS and one for PROPANE (LP). These blocks repeat identical label names
(e.g., both show "INPUT RATING", "MINIMUM INPUT RATING", "RECOVERY", "CAPACITY").

**CRITICAL RULES for dual-fuel labels:**
1. Look for "FACTORY EQUIPPED FOR [fuel type]" — extract values ONLY from that fuel's section
2. If you cannot determine which section is active, use the LEFT/FIRST section
3. Do NOT combine or average values from both fuel sections
4. "INPUT RATING" is the standard/rated input → fuel.input_btu_hr
5. "MINIMUM INPUT RATING" is the minimum modulation → fuel.minimum_input_btu_hr (a SEPARATE, LOWER number)

**Example of a dual-fuel label:**
  NATURAL GAS              |  PROPANE
  INPUT RATING 125,000     |  INPUT RATING 100,000
  MIN INPUT    25,000      |  MIN INPUT    20,000
  RECOVERY     145 GPH     |  RECOVERY     120 GPH

If "FACTORY EQUIPPED FOR NATURAL GAS", extract from the Natural Gas column only:
  fuel.input_btu_hr: 125000, fuel.minimum_input_btu_hr: 25000, water.recovery_rate_gph: 145`
}

/**
 * Generate the multi-column layout awareness section.
 * Used by both GPT and Claude prompts.
 */
export function buildMultiColumnGuidance(): string {
  return `## MULTI-COLUMN LAYOUT AWARENESS (CRITICAL)

Equipment nameplates often use 2-4 COLUMN layouts WITHOUT visible gridlines or borders.
Do NOT read straight across the full width as a single line of text.

**SPATIAL PROXIMITY RULE**: A value belongs to the label that is physically CLOSEST to it —
usually immediately to its RIGHT or BELOW it.

Example of a 2-column layout (no gridlines):
  MODEL NO.  SWR125N     SERIAL NO.  ABC12345
  VOLTAGE    120V        PHASE       1
  FLA        12.5        BTU/HR      250,000

CORRECT: MODEL NO.→SWR125N, SERIAL NO.→ABC12345, VOLTAGE→120V, PHASE→1
WRONG:   MODEL NO.→SWR125N ABC12345, VOLTAGE→120V 1

**How to detect columns**: Look for consistent vertical alignment of labels and values,
and large horizontal gaps separating label-value pairs on the same line.`
}
